import { HttpError } from '@shared/interfaces/error.interface';
import { TaskSidenavService } from '@shared/modules/task-sidenav/services/task-sidenav.service';
import { TaskViewState } from '@shared/enums/task-view-state.enum';
import { TranslateService } from '@ngx-translate/core';
import { Component, OnDestroy, ViewChild } from '@angular/core';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { SnackBarService } from '@core/services/snackbar.service';
import { ListTaskService } from '@shared/modules/list/services/list-task.service';
import { Task } from '@shared/models/task.model';
import { TaskSidenavManager } from '../../managers/task-sidenav.manager';
import { TaskType } from '@shared/enums/task-type.enum';
import { TASK_SIDENAV_FORM } from '../../const/task-sidenav-form';
import { SubtasksService } from '@shared/modules/subtasks/subtasks.service';
import { MatDialog } from '@angular/material/dialog';
import { Config } from '@shared/configs/config';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { TaskSidenavWhiteContentComponent } from './components/task-sidenav-white-content/task-sidenav-white-content.component';

@Component({
  selector: 'task-sidenav-white',
  styleUrls: ['./task-sidenav-white.component.scss'],
  templateUrl: './task-sidenav-white.component.html'
})
export class TaskSidenavWhiteComponent implements OnDestroy {
  ButtonTypes = ButtonTypes;
  ButtonSize = ButtonSize;
  taskManager: TaskSidenavManager = new TaskSidenavManager();
  @ViewChild(TaskSidenavWhiteContentComponent) contentComponent?: TaskSidenavWhiteContentComponent;

  constructor(
    private t: TranslateService,
    private s: SnackBarService,
    public service: TaskSidenavService,
    public listTaskService: ListTaskService,
    private subtasksService: SubtasksService,
    private dialog: MatDialog
  ) {}

  get shouldShowInlineSaveFooter(): boolean {
    return !!this.contentComponent?.hasPendingInlineChanges;
  }

  submitForm() {
    if (!!this.service?.task?.isDeleted) return;

    if (this.shouldShowInlineSaveFooter) {
      this.contentComponent?.saveInlineChanges();
      return;
    }

    this.service.defaultGroupID = this.service.form.get('groupId').value;
    this.service.projectID = this.service.form.get('projectId').value;
    this.service.nextTaskCreation = true;
    Object.keys(this.service.TASK_SIDENAV_FORM).forEach((key: string) => {
      this.service.form.get(key)?.markAsTouched();
      this.service.form.get(key)?.updateValueAndValidity({ emitEvent: false });
    });
    this.service.formSubmitted.next(true);
    this.service.isFormValid ? this.saveForm() : null;
  }

  discardInlineChanges() {
    this.contentComponent?.discardInlineChanges();
  }

  saveForm() {
    switch (this.service.viewState) {
      case TaskViewState.CREATE:
        this.service.addNewTask(this.listTaskService.isMyWorkTaskLists).subscribe({
          next: this.successCreatingTask.bind(this),
          error: this.errorCreatingTask.bind(this)
        });
        break;
      case TaskViewState.EDIT:
        if (this.service.hasProcessTaskContext) {
          this.confirmEditSave();
          return;
        }
        this.saveEditForm();
        break;
      default:
        break;
    }
  }

  private saveEditForm() {
    this.service
      .saveForm({
        isWorkspace: this.listTaskService.isMyWorkTaskLists,
        editMode: this.service.hasProcessTaskContext ? 'COMPANY' : undefined
      })
      .subscribe({ next: this.successUpdatingTask.bind(this), error: this.errorUpdatingTask.bind(this) });
  }

  private confirmEditSave() {
    this.dialog
      .open(ConfirmModalComponent, {
        width: Config.CONFIRM_MODAL_WIDTH,
        autoFocus: false,
        data: {
          title: 'ProjectsDashboard.stageTasks.messageTaskSidenav.editConfirmTitle',
          body: 'ProjectsDashboard.stageTasks.messageTaskSidenav.editConfirmBody'
        }
      })
      .afterClosed()
      .subscribe((confirmed: boolean | undefined) => {
        if (confirmed) {
          this.saveEditForm();
        }
      });
  }

  successCreatingTask(task: Task) {
    this.s.success(this.t.instant(`Tasks.${task.type}Created`));
    this.service.form.get(TASK_SIDENAV_FORM.type).setValue(task?.type, { emitEvent: false });
    this.taskManager.createTask(this.service.projectID, this.service?.connectedTask?.id, {
      objectType: this.service.objectType,
      type: task?.type || this.service.TaskConfigType,
      taskId: task.id,
      taskGroupId:
        task?.type !== TaskType.TYPE_CHANGE
          ? this.listTaskService.isMyWorkTaskLists
            ? task?.workspaceTaskGroup?.id
            : task?.taskGroup?.id
          : null,
      showInCalendar: this.service.showInCalendar
    });
    this.subtasksService.clearCreationSubtasksList();
  }

  errorCreatingTask(err) {
    this.s.error(this.t.instant(`Tasks.errorTask`));
  }

  successUpdatingTask(task: Task) {
    this.s.success(this.t.instant(`Tasks.${task.type}Changed`));
    this.taskManager.previewTask(task);
    this.subtasksService.clearCreationSubtasksList();
  }

  errorUpdatingTask(err: HttpError) {
    this.s.error(this.t.instant(`Tasks.errorTaskUpdate`));
  }

  ngOnDestroy(): void {
    this.subtasksService.clearCreationSubtasksList();

    if (this.service.form) {
      this.service.form.reset();

      Object.keys(this.service.form.controls).forEach((key: string) => {
        const control = this.service.form.get(key);
        if (control) {
          control.markAsUntouched();
          control.markAsPristine();
          control.setErrors(null);
        }
      });

      this.service.form.updateValueAndValidity();
    }
  }
}
